import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppRoutes } from "./app-routes";
import NotFoundContainer from "../views/errors/containers/NotFoundContainer";
import { useAuthenticationState } from "../store/features/authentication/state";
import { AppRoute } from "./types";

const modules = import.meta.glob("../views/**/containers/*.tsx");

const Router = () => {
	const { state } = useAuthenticationState();
	const isAuthenticated = state.isAuthenticated();
	const profile = state.profile();

	const hasAccess = (route: AppRoute) => {
		// Check role-based access (for admin pages)
		if (route.requiredRoles && route.requiredRoles.length > 0) {
			if (!profile?.roles) return false;
			return route.requiredRoles.some((role) =>
				profile.roles.includes(role)
			);
		}
		// Check tool-based access
		if (route.toolSlug) {
			// Admins can access all tools
			if (profile?.roles?.includes("admin")) return true;
			if (!profile?.toolAccess) return false;
			return profile.toolAccess.includes(route.toolSlug);
		}
		return true;
	};

	const routes = Object.values(AppRoutes).map((route) => {
		let SectionRoute = (
			<Route path={route.url} key={route.id} element={route.component} />
		);

		if (typeof route.component === "string") {
			const modulePath = `../views/${route.component}.tsx`;
			const Component = lazy(
				() =>
					modules[modulePath]() as Promise<{ default: React.ComponentType }>
			);
			SectionRoute = (
				<Route path={route.url} key={route.id} element={<Component />} />
			);
		}

		if (route.isPrivate && !isAuthenticated) {
			return (
				<Route
					path={route.url}
					key={route.id}
					element={<Navigate to={AppRoutes.LOGIN.url} key={route.id} />}
				/>
			);
		}

		if (route.isPrivate && !hasAccess(route)) {
			return (
				<Route
					path={route.url}
					key={route.id}
					element={
						<Navigate to={AppRoutes.NOT_AUTHORIZED.url} key={route.id} />
					}
				/>
			);
		}

		return SectionRoute;
	});

	const CustomToolContainer = lazy(() => import("../views/custom-tool/containers/CustomToolContainer"));
	const AiContainer = lazy(() => import("../views/ai/containers/AiContainer"));
	const SalesforceCaseContainer = lazy(() => import("../views/salesforce-case/containers/SalesforceCaseContainer"));

	return (
		<Suspense fallback={<div />}>
			<Routes>
				{routes.map((r) => r)}
				{isAuthenticated && (
					<>
						<Route path="/chat/:id" key="chat-direct" element={<AiContainer />} />
						<Route path="/chat/:id/shared" key="chat-shared" element={<AiContainer />} />
						{/* Case Triage deep link — tool access is enforced by the API (403) */}
						<Route path="/salesforce-case/:caseNumber" key="salesforce-case-direct" element={<SalesforceCaseContainer />} />
						<Route path="/tools/:slug" key="custom-tool" element={<CustomToolContainer />} />
					</>
				)}
				{!isAuthenticated && (
					<>
						<Route path="/chat/:id" key="chat-direct-login" element={<Navigate to={AppRoutes.LOGIN.url} />} />
						<Route path="/chat/:id/shared" key="chat-shared-login" element={<Navigate to={AppRoutes.LOGIN.url} />} />
					</>
				)}
				<Route path="*" element={<NotFoundContainer />} key="default" />
			</Routes>
		</Suspense>
	);
};

export default Router;
